import React, { createContext, useMemo, useState, useEffect, useCallback, useContext } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Create a context for theme management
export const ThemeModeContext = createContext();

const getPalette = (mode) => ({
  mode,
  ...(mode === "light"
    ? {
      primary: { main: "#EEDBC6", contrastText: "#612E2E", mainContrast: "#8C4C43" },
      secondary: { main: "#8C4C43", contrastText: "#FFFFFF" },
      background: { default: "#F7F3EF", paper: "#FFFFFF", paperAlternative: '#FFFFFF' },
      text: { primary: "#5A312F", secondary: "#7E5E59" },
      callToAction: { light: "#FFAD7A", main: "#EB494B", dark: "#EB3B3E", contrastText: "#FFFFFF" },
      success: { main: "#4CAF50", contrastText: "#FFFFFF" },
      warning: { main: "#FFB74D", contrastText: "#000000" },
      error: { main: "#D32F2F", contrastText: "#FFFFFF" },
      info: { main: "#0288D1", contrastText: "#FFFFFF" },
    }
    : {
      primary: { main: "#1E1E1E", contrastText: "#FFFFFF", mainContrast: "#333030" },
      secondary: { main: "#e8e3e3", contrastText: "#EEDBC6" },
      background: { default: "#121212", paper: "#1E1E1E", paperAlternative: '#292929' },
      text: { primary: "#FFFFFF", secondary: "#e8e3e3" },
      callToAction: { light: "#FF8A50", main: "#EB494B", dark: "#EB3B3E", contrastText: "#FFFFFF" },
      success: { main: "#81C784", contrastText: "#FFFFFF" },
      warning: { main: "#FFB74D", contrastText: "#000000" },
      error: { main: "#E57373", contrastText: "#FFFFFF" },
      info: { main: "#4FC3F7", contrastText: "#FFFFFF" },
    }),
});

const getTypography = () => ({
  fontFamily: "Roboto, sans-serif",
});

const getComponentsOverrides = (palette) => ({
  MuiButton: {
    styleOverrides: {
      root: { textTransform: "none", borderRadius: 8 },
      contained: {
        backgroundColor: palette.callToAction.main,
        color: palette.callToAction.contrastText,
        "&:hover": { backgroundColor: palette.callToAction.dark },
      },
      outlined: {
        borderColor: palette.callToAction.main,
        color: palette.callToAction.main,
        "&:hover": {
          borderColor: palette.callToAction.dark,
          backgroundColor: palette.primary.light,
          color: palette.callToAction.dark,
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        // ✅ Standard Variant: Adjust underline thickness
        "& .MuiInput-root": {
          "&:before": {
            borderBottom: `1px solid ${palette.secondary.main}`, // Default underline thickness
          },
          "&:after": {
            borderBottom: `3px solid ${palette.secondary.main}`, // Focused underline thickness
          },
          "&:hover:not(.Mui-disabled):before": {
            borderBottom: `2px solid ${palette.secondary.main}`, // Hover underline thickness
          },
        },
        // ✅ Outlined Variant: Adjust border thickness
        "& .MuiOutlinedInput-root": {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: palette.secondary.main,
            borderWidth: "2px",
          },
          "&:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline": {
            borderColor: palette.secondary.main,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: palette.secondary.main,
            borderWidth: "3px",
          },
        },
        // ✅ Label Behavior: Keep color the same and prevent washed-out effect
        "& .MuiInputLabel-root": {
          color: palette.secondary.main,
          fontSize: "1rem",
          "&.Mui-focused": {
            color: palette.secondary.main,
          },
        },
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: palette.secondary.main,
        color: palette.secondary.contrastText
      }
    }
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        color: palette.secondary.contrastText
      }
    }
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: palette.secondary.main,
          borderWidth: "2px",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: palette.secondary.main,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: palette.secondary.main,
          borderWidth: "3px",
        },
      },
    },
  },

  // 2) Keep label color consistent when you use <InputLabel> with <Select>
  MuiInputLabel: {
    styleOverrides: {
      root: {
        color: palette.secondary.main,
        fontSize: "1rem",
        "&.Mui-focused": { color: palette.secondary.main },
      },
    },
  },

  // 3) Make the dropdown chevron match
  MuiSelect: {
    styleOverrides: {
      icon: { color: palette.secondary.main },
    },
  },
});

const ThemeContextProvider = ({ children }) => {
  const [mode, setMode] = useState(() => localStorage.getItem("themeMode") || "light");

  // ✅ UseEffect to update localStorage only when `mode` changes
  useEffect(() => {
    localStorage.setItem("themeMode", mode);
  }, [mode]);

  // ✅ Memoized function to prevent unnecessary re-renders
  const toggleTheme = useCallback(() => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  }, []);

  // ✅ Memoize theme to prevent `ThemeProvider` from triggering unnecessary re-renders
  const theme = useMemo(() => {
    const palette = getPalette(mode);
    return createTheme({
      palette,
      typography: getTypography(),
      components: getComponentsOverrides(palette),
    });
  }, [mode]);

  

  return (
    <ThemeModeContext.Provider value={{ mode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useAppTheme = () => {
  return useContext(ThemeModeContext);
};

export default ThemeContextProvider;
